import { DEFAULT_HEADER, USER_TOKEN, signoutToLogin } from '@Config/userToken';

const AUTH_REFRESH_MIN_VALIDITY_MS = 60000;
const executeRequest = (url, options) => fetch(url, options);

const normalizeHeaders = (headers = {}) => {
    if (headers instanceof Headers) {
        return Object.fromEntries(headers.entries());
    }

    return { ...headers };
};

const buildAuthHeaders = (headers = {}, { hasFormDataBody = false } = {}) => {
    const customHeaders = normalizeHeaders(headers);
    const defaultHeaders = DEFAULT_HEADER.get();
    const contentType = customHeaders['Content-Type'] || customHeaders['content-type'] || defaultHeaders['Content-Type'];
    const authHeaders = {
        ...customHeaders,
        ...defaultHeaders
    };

    if (hasFormDataBody) {
        delete authHeaders['Content-Type'];
        delete authHeaders['content-type'];
        return authHeaders;
    }

    return {
        ...authHeaders,
        'Content-Type': contentType
    };
};

const buildPublicHeaders = (headers = {}, { hasFormDataBody = false } = {}) => {
    const publicHeaders = normalizeHeaders(headers);

    if (hasFormDataBody) {
        delete publicHeaders['Content-Type'];
        delete publicHeaders['content-type'];
    }

    return publicHeaders;
};

const normalizeApiConfig = (config = {}) => {
    if (typeof config === 'boolean') {
        return { auth: config };
    }

    return config || {};
};

/**
 * Ejecuta una petición HTTP utilizando fetch.
 *
 * Para peticiones autenticadas:
 * 1. Verifica preventivamente la validez del token.
 * 2. Ejecuta la petición.
 * 3. Si recibe HTTP 401, intenta renovar forzosamente el token.
 * 4. Si la renovación funciona, reintenta una única vez.
 * 5. Si la respuesta continúa siendo HTTP 401, puede redirigir al login.
 *
 * Esta función no lanza automáticamente un error para respuestas HTTP 4xx
 * o 5xx. El consumidor debe evaluar response.ok o response.status.
 *
 * @param {string|URL|Request} url
 * Dirección o recurso solicitado.
 *
 * @param {RequestInit} [options={}]
 * Opciones nativas de fetch.
 *
 * @param {ApiAuthConfig} [config={}]
 * Configuración de autenticación y tratamiento de respuestas HTTP 401.
 *
 * @returns {Promise<Response>}
 * Respuesta HTTP sin procesar.
 *
 * @throws {TypeError}
 * Puede lanzar errores propios de fetch, por ejemplo ante problemas de red.
 */
export const apiFetch = async (url, options = {}, { auth = true, retryOnUnauthorized = true, redirectOnUnauthorized = true } = {}) => {
    const hasFormDataBody = typeof FormData !== 'undefined' && options.body instanceof FormData;
    const buildRequestOptions = () => ({
        ...options,
        headers: auth
            ? buildAuthHeaders(options.headers, { hasFormDataBody })
            : buildPublicHeaders(options.headers, { hasFormDataBody })
    });

    if (!auth) {
        return executeRequest(url, buildRequestOptions());
    }

    await USER_TOKEN.refresh_token({
        minValidityMs: AUTH_REFRESH_MIN_VALIDITY_MS,
        redirectOnFailure: false,
        request: executeRequest
    });

    let response = await executeRequest(url, buildRequestOptions());

    if (response.status === 401 && retryOnUnauthorized) {
        const refreshed = await USER_TOKEN.refresh_token({
            force: true,
            redirectOnFailure: false,
            request: executeRequest
        });

        if (refreshed) {
            response = await executeRequest(url, buildRequestOptions());
        }
    }

    if (response.status === 401 && redirectOnUnauthorized) {
        signoutToLogin();
    }

    return response;
};

export async function parseApiResponse(response) {
    if (response.status === 204) return null;
    if (response.status === 401) return undefined;

    const text = await response.text();

    if (!text) return null;

    try {
        return JSON.parse(text);
    } catch (_) {
        return text;
    }
}

async function toJson(url, options = {}, config = {}) {
    const response = await apiFetch(url, options, normalizeApiConfig(config));
    return parseApiResponse(response);
}

export function apiGet(url, config = {}) {
    const { signal, ...rest } = normalizeApiConfig(config);
    return toJson(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        signal,
    }, rest);
}

export function apiPost(url, bodyData, config = {}, contentTypeOverride) {
    const normalizedConfig = normalizeApiConfig(config);
    const contentType = contentTypeOverride || normalizedConfig.contentType || 'application/json';
    const { contentType: _, ...fetchConfig } = normalizedConfig;

    return toJson(url, {
        method: 'POST',
        headers: { 'Content-Type': contentType },
        body: JSON.stringify(bodyData)
    }, fetchConfig);
}

export function apiPut(url, bodyData, config = {}, contentTypeOverride) {
    const normalizedConfig = normalizeApiConfig(config);
    const contentType = contentTypeOverride || normalizedConfig.contentType || 'application/json';
    const { contentType: _, ...fetchConfig } = normalizedConfig;

    return toJson(url, {
        method: 'PUT',
        headers: { 'Content-Type': contentType },
        body: JSON.stringify(bodyData)
    }, fetchConfig);
}

export function apiDelete(url, config = {}) {
    return toJson(url, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' }
    }, normalizeApiConfig(config));
}
